/**
 * Custom Sticky Images & Text
 *
 * IntersectionObserver-driven section where text stays sticky
 * while images scroll. Active slide detected by viewport center.
 *
 * Features:
 * - IntersectionObserver with centered rootMargin
 * - Dot navigation with scrollIntoView
 * - Theme editor load/unload support
 * - Cleanup on destroy to prevent memory leaks
 */

(function () {
  'use strict';

  const SELECTORS = {
    section: '[data-custom-sit]',
    imageWrap: '[data-custom-sit-image]',
    panel: '[data-custom-sit-panel]',
    dot: '[data-custom-sit-dot]',
    dots: '[data-custom-sit-dots]',
  };

  const CLASSES = {
    active: 'is-active',
  };

  class NSStickyImagesText {
    constructor(sectionEl) {
      this.section = sectionEl;
      if (!this.section) return;

      this.images = Array.from(this.section.querySelectorAll(SELECTORS.imageWrap));
      this.panels = Array.from(this.section.querySelectorAll(SELECTORS.panel));
      this.dots = Array.from(this.section.querySelectorAll(SELECTORS.dot));
      this.activeIndex = 0;
      this.observer = null;

      if (this.images.length === 0) return;

      this.init();
    }

    init() {
      this.setActive(0);
      this.setupObserver();
      this.setupDotNav();
    }

    setupObserver() {
      // Only observe on desktop (990px+)
      if (window.innerWidth < 990) return;

      const options = {
        root: null,
        rootMargin: '-40% 0px -40% 0px',
        threshold: 0,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = this.images.indexOf(entry.target);
            if (index !== -1) {
              this.setActive(index);
            }
          }
        });
      }, options);

      this.images.forEach((img) => this.observer.observe(img));
    }

    setActive(index) {
      if (index < 0 || index >= this.images.length) return;
      this.activeIndex = index;

      this.panels.forEach((panel, i) => {
        panel.classList.toggle(CLASSES.active, i === index);
      });

      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle(CLASSES.active, isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    setupDotNav() {
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          const target = this.images[i];
          if (!target) return;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.setActive(i);
        });

        // Arrow key navigation within tablist
        dot.addEventListener('keydown', (e) => {
          let nextIndex = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            nextIndex = (i + 1) % this.dots.length;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            nextIndex = (i - 1 + this.dots.length) % this.dots.length;
          } else if (e.key === 'Home') {
            e.preventDefault();
            nextIndex = 0;
          } else if (e.key === 'End') {
            e.preventDefault();
            nextIndex = this.dots.length - 1;
          }

          if (nextIndex !== null) {
            this.dots[nextIndex].focus();
            this.dots[nextIndex].click();
          }
        });
      });
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  /* ============================================
     Initialization & Theme Editor Support
     ============================================ */

  const instances = new Map();

  function initSection(sectionEl) {
    if (!sectionEl) return;
    const id = sectionEl.dataset.sectionId;
    // Destroy existing instance if any
    if (instances.has(id)) {
      instances.get(id).destroy();
    }
    instances.set(id, new NSStickyImagesText(sectionEl));
  }

  function destroySection(sectionId) {
    if (instances.has(sectionId)) {
      instances.get(sectionId).destroy();
      instances.delete(sectionId);
    }
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.section).forEach(initSection);
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor events
  if (Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const sectionEl = e.target.querySelector(SELECTORS.section);
      if (sectionEl) initSection(sectionEl);
    });

    document.addEventListener('shopify:section:unload', (e) => {
      const sectionEl = e.target.querySelector(SELECTORS.section);
      if (sectionEl) destroySection(sectionEl.dataset.sectionId);
    });
  }
})();
